import { Shield, Globe, Handshake, Package, Check, ArrowRight } from "lucide-react";
import { Reveal } from "../lib/motion";
import { PRODUCTS, FEATURES, QUALITY_CHECKS, MARKETS, BUYER_TYPES, REGIONS, IMAGES } from "../data";

const ICONS = { Shield, Globe, Handshake, Package };

export function TrustStrip() {
  const items = ["Premium Quality", "Ethical Sourcing", "Export Expertise", "Long-Term Partnerships"];
  return (
    <div className="bg-white border-y border-[#E7E1D6]" data-testid="trust-strip">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map((t, i) => (
          <Reveal key={t} delay={i * 0.08} className="flex items-center gap-3">
            <Check size={18} className="text-[#B08D57]" strokeWidth={2} />
            <span className="font-head font-semibold text-sm md:text-[15px] text-[#111]">{t}</span>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

export function Manifesto() {
  return (
    <section id="about" className="bg-[#F8F7F3] py-24 md:py-40">
      <div className="max-w-[900px] mx-auto px-6 text-center">
        <Reveal>
          <p className="overline text-[#B08D57] mb-8">01 — The Origin</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="section-title font-display text-[#111] mb-10">
            Building Global Trust Through Indian Excellence
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="font-body text-lg md:text-xl text-[#444] leading-relaxed max-w-[720px] mx-auto">
            AGRAGA Velora Exports exists to connect the world&apos;s markets with India&apos;s finest products.
            Every partnership is built on integrity, uncompromising quality, and long-term trust.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <p className="font-display italic text-2xl md:text-3xl text-[#111] mt-12 leading-snug">
            We do not simply export products.<br />
            We represent India&apos;s reputation across global markets.
          </p>
        </Reveal>
        <Reveal delay={0.4}>
          <div className="flex flex-col sm:flex-row justify-center gap-6 mt-14">
            <button
              data-testid="manifesto-products-btn"
              onClick={() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full bg-[#111] text-white font-head font-semibold px-8 py-4 transition-colors duration-300 hover:bg-[#B08D57]"
            >
              Explore Products
            </button>
            <button
              data-testid="manifesto-partner-btn"
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full border border-[#111] text-[#111] font-head font-semibold px-8 py-4 transition-colors duration-300 hover:bg-[#111] hover:text-white"
            >
              Become a Partner
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function Portfolio({ onQuote }) {
  return (
    <section id="products" className="bg-white py-24 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="max-w-[720px] mb-16">
          <Reveal><p className="overline text-[#B08D57] mb-6">02 — The Portfolio</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="section-title font-display text-[#111] mb-6">Our Premium Export Portfolio</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="font-body text-lg text-[#666] leading-relaxed">
              Carefully sourced from India&apos;s finest regions and delivered to international markets
              with uncompromising quality standards.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.name} delay={(i % 3) * 0.1} data-testid={`product-card-${i}`}>
              <div className="group relative overflow-hidden bg-[#F8F7F3] border border-[#E7E1D6]">
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-70" />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_center,transparent_30%,rgba(0,0,0,0.35)_100%)]" />
                </div>
                <div className="absolute bottom-0 inset-x-0 p-7">
                  <p className="overline text-[#E7C99A] mb-2 text-[11px]">{p.tag}</p>
                  <h3 className="font-display text-white text-3xl">{p.name}</h3>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="flex justify-center mt-16">
            <button
              data-testid="catalogue-btn"
              onClick={onQuote}
              className="group inline-flex items-center gap-3 rounded-full bg-[#111] text-white font-head font-semibold px-9 py-4 transition-colors duration-300 hover:bg-[#B08D57]"
            >
              View Complete Catalogue
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function Features() {
  return (
    <section className="bg-[#F8F7F3] py-24 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal><p className="overline text-[#B08D57] mb-6">03 — The Assurance</p></Reveal>
        <Reveal delay={0.1}>
          <h2 className="section-title font-display text-[#111] mb-16 max-w-[720px]">
            Why Global Buyers Choose AGRAGA Velora
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E7E1D6] border border-[#E7E1D6]">
          {FEATURES.map((f, i) => {
            const Icon = ICONS[f.icon];
            return (
              <Reveal key={f.title} delay={i * 0.08} className="bg-white">
                <div className="group h-full p-8 lg:p-10 transition-colors duration-500 hover:bg-[#111]">
                  <Icon size={30} strokeWidth={1.4} className="text-[#B08D57]" />
                  <h3 className="font-head font-bold text-xl text-[#111] mt-8 mb-4 transition-colors duration-500 group-hover:text-white">
                    {f.title}
                  </h3>
                  <p className="font-body text-[15px] text-[#666] leading-relaxed transition-colors duration-500 group-hover:text-white/70">
                    {f.body}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function Quality() {
  return (
    <section id="quality" className="relative bg-[#111] py-24 md:py-40 grain overflow-hidden">
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <Reveal><p className="overline text-[#B08D57] mb-6">04 — The Standard</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="section-title font-display text-[#F8F7F3] mb-8">
              Every Shipment Reflects Our Name.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="font-display italic text-2xl text-[#B08D57] mb-12 leading-snug">
              Quality is not a process at AGRAGA Velora.<br />It is our reputation.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
            {QUALITY_CHECKS.map((c, i) => (
              <Reveal key={c} delay={0.2 + i * 0.06} className="flex items-center gap-4 border-b border-white/10 pb-4">
                <Check size={18} className="text-[#B08D57] shrink-0" strokeWidth={2} />
                <span className="font-body text-[15px] text-[#F8F7F3]/90">{c}</span>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.15}>
          <div className="relative aspect-[4/5] overflow-hidden">
            <img src={IMAGES.quality} alt="Quality inspection" className="w-full h-full object-cover" />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function Kerala() {
  return (
    <section className="bg-[#F8F7F3] grid lg:grid-cols-2 items-stretch">
      <div className="relative min-h-[420px] lg:min-h-[640px] overflow-hidden">
        <img src={IMAGES.kerala} alt="Hills of Kerala" className="absolute inset-0 w-full h-full object-cover" />
      </div>
      <div className="flex items-center px-6 md:px-16 py-20 lg:py-0">
        <div className="max-w-[520px]">
          <Reveal><p className="overline text-[#B08D57] mb-6">05 — The Source</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="section-title font-display text-[#111] mb-8">
              From the Hills of Kerala to the World
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="font-body text-lg text-[#666] leading-relaxed">
              Our sourcing network is rooted in regions known for exceptional agricultural heritage.
              Every product reflects the craftsmanship of Indian growers and the standards expected
              by global markets.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function Markets() {
  return (
    <section id="markets" className="bg-white py-24 md:py-36">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
        <Reveal><p className="overline text-[#B08D57] mb-6">06 — The Reach</p></Reveal>
        <Reveal delay={0.1}>
          <h2 className="section-title font-display text-[#111] mb-12">Serving Buyers Across Global Markets</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-[#E7E1D6] border border-[#E7E1D6] mb-14">
            {REGIONS.map((r) => (
              <div
                key={r}
                data-testid={`region-${r}`}
                className="bg-white py-8 px-4 font-head font-semibold text-[15px] text-[#111] transition-colors duration-300 hover:bg-[#111] hover:text-white"
              >
                {r}
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="flex flex-wrap justify-center gap-3 mb-16">
            {MARKETS.map((m) => (
              <span
                key={m}
                data-testid={`market-${m}`}
                className="rounded-full border border-[#E7E1D6] bg-[#F8F7F3] px-6 py-3 font-head font-semibold text-sm text-[#111] transition-colors duration-300 hover:border-[#B08D57] hover:text-[#B08D57]"
              >
                {m}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="border-t border-[#E7E1D6] pt-12">
            <p className="overline text-[#666] mb-8">We Partner With</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-y-6 gap-x-4">
              {BUYER_TYPES.map((b) => (
                <span key={b} className="font-display text-xl md:text-2xl text-[#111]">{b}</span>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function Promise() {
  const words = ["Trust. Delivered.", "Integrity.", "Quality.", "Reliability.", "Partnership."];
  return (
    <section className="bg-[#F8F7F3] py-24 md:py-36 overflow-hidden">
      <div className="marquee-track mb-16" aria-hidden="true">
        {[0, 1].map((k) => (
          <span key={k} className="flex">
            {words.map((w, i) => (
              <span key={i} className="font-display text-[#111]/10 text-6xl md:text-8xl px-8">
                {w}
              </span>
            ))}
          </span>
        ))}
      </div>
      <div className="max-w-[880px] mx-auto px-6 text-center">
        <Reveal>
          <h2 className="font-display text-[#111] text-5xl md:text-7xl mb-10">Trust. Delivered.</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="font-display italic text-2xl md:text-3xl text-[#B08D57] mb-8 leading-snug">
            Our clients are not buying a shipment.<br />They are choosing a partner.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <p className="font-body text-lg text-[#666] leading-relaxed max-w-[640px] mx-auto">
            Every product we export carries our commitment to integrity, quality, reliability, and
            long-term relationships.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
